import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { isRegisteredCustomTarget } from '../constants/customActionRegistry';
import { ButtonAction, ButtonActionType } from '../types';

const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export enum ComponentActionResultKind {
  SIDE_EFFECT = 'side_effect',
  DISMISS = 'dismiss',
  CUSTOM = 'custom'
}

/** Result of handling a button action — callers branch on `kind`. */
export type ComponentActionResult =
  | { kind: ComponentActionResultKind.SIDE_EFFECT }
  | { kind: ComponentActionResultKind.DISMISS }
  | { kind: ComponentActionResultKind.CUSTOM; target: string };

const useComponentAction = () => {
  const navigate = useNavigate();

  const isActionValid = useCallback((button: ButtonAction): boolean => {
    const { action } = button;
    if (action.type === ButtonActionType.EXTERNAL_LINK)
      return isValidUrl(action.target);
    if (action.type === ButtonActionType.INTERNAL_ROUTE)
      return action.target.length > 0;
    if (action.type === ButtonActionType.CUSTOM)
      return isRegisteredCustomTarget(action.target);
    if (action.type === ButtonActionType.DISMISS) return true;
    return false;
  }, []);

  const handleAction = useCallback(
    (button: ButtonAction): ComponentActionResult => {
      const { action } = button;

      if (action.type === ButtonActionType.INTERNAL_ROUTE) {
        navigate(action.target);
        return { kind: ComponentActionResultKind.SIDE_EFFECT };
      }

      if (
        action.type === ButtonActionType.EXTERNAL_LINK &&
        isValidUrl(action.target)
      ) {
        window.open(action.target, '_blank', 'noopener,noreferrer');
        return { kind: ComponentActionResultKind.SIDE_EFFECT };
      }

      if (action.type === ButtonActionType.DISMISS) {
        return { kind: ComponentActionResultKind.DISMISS };
      }

      if (
        action.type === ButtonActionType.CUSTOM &&
        isRegisteredCustomTarget(action.target)
      ) {
        return {
          kind: ComponentActionResultKind.CUSTOM,
          target: action.target
        };
      }

      return { kind: ComponentActionResultKind.SIDE_EFFECT };
    },
    [navigate]
  );

  return { handleAction, isActionValid };
};

export default useComponentAction;
